import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Icon,
  HStack,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Flex,
  Divider,
} from '@chakra-ui/react';
import { FiAlertTriangle, FiCheckCircle, FiTrendingUp, FiShield, FiFileText } from 'react-icons/fi';

const SectionTitle = ({ children }) => (
  <Heading as="h2" size="lg" color="gray.800" borderBottom="2px solid" borderColor="blue.500" pb={1} mb={4}>
    {children}
  </Heading>
);

const SubHeading = ({ children }) => (
  <Heading as="h3" size="md" color="blue.700" mt={4} mb={2}>
    {children}
  </Heading>
);

const ValueProposition = ({ icon, title, description }) => (
  <HStack align="start" spacing={3} p={3} bg="white" borderRadius="md" shadow="sm">
    <Icon as={icon} color="blue.500" w={6} h={6} mt={1} />
    <VStack align="start" spacing={1}>
      <Text fontWeight="bold" fontSize="md" color="gray.800">{title}</Text>
      <Text color="gray.600" fontSize="sm">{description}</Text>
    </VStack>
  </HStack>
);

const ChallengeItem = ({ text }) => (
  <HStack align="start" spacing={2} mb={2}>
    <Icon as={FiAlertTriangle} color="orange.500" w={4} h={4} mt={1} flexShrink={0} />
    <Text color="gray.700" fontSize="sm">{text}</Text>
  </HStack>
);

const NatusProposal = () => {
  return (
    <Box bg="white" minH="100vh">
      <Container maxW="container.lg" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box textAlign="center" p={4} bg="blue.800" color="white" borderRadius="md">
            <Heading as="h1" size="xl">
              Revedy AI: EEG, EMG, IOM Coding Automation and Medical Necessity for Natus Customers
            </Heading>
            <Text fontSize="lg" mt={1}>A Revedy Solution Blueprint</Text>
          </Box>

          {/* Section 1: What We Do */}
          <Box>
            <SectionTitle>What the Revedy AI RCM Agent Does</SectionTitle>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <ValueProposition
                icon={FiFileText}
                title="Analyze Clinical Data"
                description="We ingest the complete case file—notes, reports, and raw device data—to create an unimpeachable, audit-proof record of every procedure."
              />
              <ValueProposition
                icon={FiShield}
                title="Enforce Payer Rules"
                description="Our AI validates claims against thousands of payer-specific rules *before* submission to ensure compliance and prevent denials."
              />
              <ValueProposition
                icon={FiTrendingUp}
                title="Optimize Revenue"
                description="We ensure every billable service is accurately captured, coded, and justified to maximize reimbursement and stop revenue leakage."
              />
            </SimpleGrid>
          </Box>

          {/* Section 2: The Challenges */}
          <Box p={4} borderWidth="1px" borderRadius="md" bg="white" shadow="md">
            <Heading as="h2" size="lg" color="gray.800" mb={4}>The Reimbursement Challenges Natus Customers Face</Heading>
            
            <SubHeading>EEG Billing</SubHeading>
            <ChallengeItem text="Billing the wrong long-term EEG code for multi-day studies (e.g., two units of 95720 instead of one unit of 95722)." />
            <ChallengeItem text="Ambulatory EEG claims denied for 'medical necessity' due to a missing prior, inconclusive routine EEG." />
            <ChallengeItem text="Professional component (-26) claims denied due to payer-specific modifier rules that vary by state." />
            
            <SubHeading>EMG/NCS Billing</SubHeading>
            <ChallengeItem text="Billing EMG 'add-on' codes (e.g., +95886) without a primary NCS code, causing guaranteed denials." />
            <ChallengeItem text="Under-coding comprehensive studies by billing for one limb (95860) when two were performed (95861)." />
            <ChallengeItem text="Failing to bill for EMG guidance (+95874) during procedures like Botox injections, leaving money on the table." />

            <SubHeading>IONM Billing</SubHeading>
            <ChallengeItem text="Claims for remote monitoring (+95941) denied by major payers like UHC who mandate the restrictive G0453 code." />
            <ChallengeItem text="Inability to meet the massive documentation burden required to prove 'exclusive attention' for G0453, making it nearly impossible to bill." />
          </Box>

          {/* Section 3: The Revedy Solution */}
          <Box>
            <SectionTitle>The Revedy Solution</SectionTitle>
            <Text fontSize="md" color="gray.700" mb={4}>
              Revedy provides a unified AI platform that directly solves these complex challenges, ensuring the clinical value of Natus technology is converted into reliable revenue.
            </Text>
            <List spacing={3} fontSize="md">
              <ListItem>
                <HStack align="start"><ListIcon as={FiCheckCircle} color="green.500" mt={1} /><Text><Text as="span" fontWeight="bold">Automated, Accurate Coding:</Text> Our AI analyzes the full case file, including device data, to select the correct CPT codes based on study duration, scope, and bundling rules. It prevents under-coding and eliminates common errors like billing the wrong VEEG code or forgetting a primary NCS code.</Text></HStack>
              </ListItem>
              <ListItem>
                <HStack align="start"><ListIcon as={FiCheckCircle} color="green.500" mt={1} /><Text><Text as="span" fontWeight="bold">Payer Policy Enforcement:</Text> We maintain a vast library of payer-specific rules. The platform automatically substitutes codes (e.g., 95941 to G0453 for UHC) and applies the correct modifiers based on the payer, preventing administrative denials.</Text></HStack>
              </ListItem>
              <ListItem>
                <HStack align="start"><ListIcon as={FiCheckCircle} color="green.500" mt={1} /><Text><Text as="span" fontWeight="bold">Pre-Submission Validation:</Text> Before a claim is sent, we check it against medical necessity policies, flagging cases that lack required prior studies or a covered diagnosis, giving providers a chance to fix issues that would otherwise lead to denial.</Text></HStack>
              </ListItem>
              <ListItem>
                <HStack align="start"><ListIcon as={FiCheckCircle} color="green.500" mt={1} /><Text><Text as="span" fontWeight="bold">Compliance Guardrails:</Text> We help providers navigate complex scenarios like automated AI analysis, ensuring they bill correctly for their professional expertise.</Text></HStack>
              </ListItem>
            </List>
          </Box>

          {/* Section 4: Conclusion */}
          <Box>
            <SectionTitle>Strategic Support for Commercial Acceleration</SectionTitle>
            <Text fontSize="md" color="gray.700">
              Revedy can provide comprehsive, up-to-date support for providers with complex coding and billing requirements. 
              WIth a robust UI, and easy to integrate APIs, Revedy makes it simple for Natus customers to optimize reimbursement and reduce riwk with accurate and auditable outpus.
            </Text>
          </Box>
        </VStack>
        <Divider my={6} borderColor="gray.300" />
        <Flex justify="space-between" align="center">
          <Text fontSize="xs" color="gray.500">https://revedy.io</Text>
          <Text fontSize="xs" color="gray.500">team@revedy.io</Text>
        </Flex>
      </Container>
    </Box>
  );
};

export default NatusProposal;